// @ts-strict-ignore
import type { PayloadAction } from "@reduxjs/toolkit";
import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  combineReducers,
  createSelector,
  createAction,
} from "@reduxjs/toolkit";
import axios from "axios";
import type { Content as ContentEntity, ProjectLanguageDatum } from "src/model/api";
import type { Project } from "../util/dataTypes/Project";
import type { FlatGraphResponse } from "../util/dataTypes/FlatGraphResponse";
import { hasContentTypeObject } from "../util/dataTypes/FlatGraphResponse";
import type { Action } from "../util/dataTypes/Action";
import type { Content } from "../util/dataTypes/Content";
import type {
  ContentType,
  ContentTypeAsset,
  ContentTypeScene,
  ModelName,
  ValidContentType,
} from "../util/dataTypes/ContentType";
import { buildGraph } from "../util/graphBuilder";
import { normalizeContentTypeObject } from "../util/schemas";
import apiUrls from "../../../api";
import type { RootState } from "../../../common/state/store";
import type { IAppLoaderAction } from "../../../common/state/loaderHandleMiddleware";
import type { IProjectLanguageData } from "src/model/model";
import { stripEmptyMediaRelations } from "../util/mediaRelationKeys";
import { BASE_CONTENT_FIELD_KEYS } from "../util/advancedFieldsCatalog";
import { omitStrapiEntityMetaKeys, resolveDocumentId } from "src/common/util/strapiDocumentId";

const throwError = (_error: any, _userMessage = "") => {
  // const a = getThrowErrorFuncEditProjectScenes(store.);
  /*const func: any = dirtyGetThrowErrorFuncEditProjectScenes();
  func(error);*/
};

type CurrentProjectLanguages = {
  language?: { id?: number } | number;
  enabled_languages?: Array<{ id: number }>;
};

export const resolvePrimaryLanguageId = (
  currentProject: CurrentProjectLanguages | undefined,
): number | undefined =>
  typeof currentProject?.language === "object" && currentProject.language !== null
    ? currentProject.language.id
    : Number.isFinite(Number(currentProject?.language))
    ? Number(currentProject?.language)
    : currentProject?.enabled_languages?.[0]?.id;

export const loadGraphLanguages = createAsyncThunk(
  "data/loadGraphLanguages",
  async ({ projectId, languageIds }: { projectId: number; languageIds: number[] }) => {
    if (languageIds.length === 0) return { projectLanguageDatas: [] };
    const response = await axios.get<{ projectLanguageDatas: IProjectLanguageData[] }>(
      `${
        apiUrls.projects.graph.flat
      }/${projectId}?isForGraph=true&lenient=true&parts=pld&pldLanguages=${languageIds.join(",")}`,
    );
    return response.data;
  },
);

export const loadGraph = createAsyncThunk(
  "data/loadGraph",
  async (projectId: number, { getState, dispatch }) => {
    try {
      const state = getState() as RootState;
      const currentProject = state.projects.currentProject as CurrentProjectLanguages | undefined;
      const languageId = resolvePrimaryLanguageId(currentProject);
      const pldLanguagesParam = languageId ? `&pldLanguages=${languageId}` : "";
      const response = await axios.get<FlatGraphResponse>(
        `${apiUrls.projects.graph.flat}/${projectId}?isForGraph=true&lenient=true${pldLanguagesParam}`,
      );
      const otherLanguageIds = (currentProject?.enabled_languages ?? [])
        .map((l) => Number(l.id))
        .filter((id) => Number.isFinite(id) && id !== Number(languageId));
      if (languageId && otherLanguageIds.length > 0) {
        setTimeout(() => {
          const loadedProjectId = (getState() as RootState).graphLegacy.project?.id;
          if (Number(loadedProjectId) !== Number(projectId)) return;
          void dispatch(loadGraphLanguages({ projectId, languageIds: otherLanguageIds }));
        }, 0);
      }
      return response;
    } catch (error) {
      throwError(error);
      return Promise.reject(error);
    }
  },
);

export const loadGraphLanguage = createAsyncThunk(
  "data/loadGraphLanguage",
  async ({ projectId, languageId }: { projectId: number; languageId: number }) => {
    const response = await axios.get<{ projectLanguageDatas: IProjectLanguageData[] }>(
      `${apiUrls.projects.graph.flat}/${projectId}?isForGraph=true&lenient=true&parts=pld&pldLanguages=${languageId}`,
    );
    return response.data;
  },
);

export const actionUpsertedLocally = createAction<Action>("graph/actionUpsertedLocally");
export const actionDeletedLocally = createAction<number>("graph/actionDeletedLocally");
export const contentTimecodePatchedLocally = createAction<{
  contentId: number;
  timeCode: number;
}>("graph/contentTimecodePatchedLocally");
export const contentRenamedLocally = createAction<{
  contentId: number;
  name: string;
}>("graph/contentRenamedLocally");

export const interactionCreatedLocally = createAction<{
  content: any;
  interaction: any;
  modelName: string;
  action: Action;
  plds: any[];
  projectLanguageId: number | null;
}>("graph/interactionCreatedLocally");

export const contentDeletedLocally = createAction<number>("graph/contentDeletedLocally");

export const checkValidityOnly = createAsyncThunk(
  "data/checkValidityOnly",
  async (projectId: number) => {
    try {
      const response = await axios.get(
        `${apiUrls.projects.graph.flat}/${projectId}?checkValidityOnly=true&isForGraph=true`,
      );

      // Extract only the necessary serializable data
      const { data } = response;
      return { data };
    } catch (error) {
      throwError(error);
      return Promise.reject(error);
    }
  },
);

export const copySceneToProject = createAsyncThunk(
  "graph/copySceneToProject",
  async ({
    sourceContentSceneId,
    sourceProjectId,
    targetProjectId,
    replaceContentSceneId,
  }: {
    sourceContentSceneId: number;
    sourceProjectId: number;
    targetProjectId: number;
    replaceContentSceneId?: number;
  } & IAppLoaderAction) => {
    const response = await axios.post(apiUrls.projects.copySceneToProject, {
      sourceContentSceneId,
      sourceProjectId,
      targetProjectId,
      replaceContentSceneId,
    });
    return { data: response.data };
  },
);

export const fetchUnlinkedScenes = createAsyncThunk(
  "graph/fetchUnlinkedScenes",
  async ({ projectId }: { projectId: number } & IAppLoaderAction) => {
    const response = await axios.get(`${apiUrls.projects.unlinkedScenes}/${projectId}`);
    return { data: response.data };
  },
);

export type SceneNavEvent = {
  eventTypeId: number;
  name: string;
  occupied: boolean;
  activateActionId?: number;
  currentTargetSceneContentId?: number;
  currentTargetSceneName?: string | null;
};

export type SceneNavInteraction = {
  contentId: number;
  modelName: string;
  name: string;
  events: SceneNavEvent[];
};

export type SceneNavigationOptions = {
  sceneIsVideo: boolean;
  videoEnd: SceneNavEvent | null;
  interactions: SceneNavInteraction[];
};

export type RelinkTriggerMode = "videoEnd" | "interaction" | "newButton";

export type RelinkTrigger = {
  triggerMode: RelinkTriggerMode;
  triggerContentId?: number;
  eventTypeId?: number;
  activateActionId?: number;
  move?: {
    bridgeActivateActionIds: number[];
    bridgeToContentId: number | null;
    removeActivateActionIds?: number[];
  };
};

export type RelinkOperation = "add" | "replace" | "insert";

export type RelinkUndoPayload = {
  createdActionIds: number[];
  updatedActions: { id: number; previousAffectedContentId: number | null }[];
  deletedActions: {
    is_activate: boolean;
    eventTypeId: number | null;
    affectContentId: number | null;
    affectedContentId: number | null;
  }[];
  newButtonContentIds: number[];
  sceneContentIds: number[];
  reparentedContents?: { contentId: number; previousParentContentId: number | null }[];
};

export const fetchSceneNavigationOptions = createAsyncThunk(
  "graph/fetchSceneNavigationOptions",
  async ({ sceneContentSceneId }: { sceneContentSceneId: number } & IAppLoaderAction) => {
    const response = await axios.get(
      `${apiUrls.projects.sceneNavigationOptions}/${sceneContentSceneId}`,
    );
    return response.data as SceneNavigationOptions;
  },
);

export const relinkScene = createAsyncThunk(
  "graph/relinkScene",
  async ({
    projectId,
    targetContentId,
    operation,
    entry,
    exit,
  }: {
    projectId: number;
    targetContentId: number;
    operation: RelinkOperation;
    entry: RelinkTrigger & { sourceContentSceneId: number };
    exit?: RelinkTrigger;
  } & IAppLoaderAction) => {
    const response = await axios.post(apiUrls.projects.relinkScene, {
      projectId,
      targetContentId,
      operation,
      entry,
      exit,
    });
    return { data: response.data };
  },
);

export const setStartScene = createAsyncThunk(
  "graph/setStartScene",
  async ({ contentSceneId }: { contentSceneId: number } & IAppLoaderAction, { getState }) => {
    const state = getState() as RootState;
    const contentSceneObject = state.graphLegacy.contentTypes.entities[
      `content-scene-${contentSceneId}`
    ]?.object as { documentId?: string } | undefined;
    const documentId = await resolveDocumentId(
      `${apiUrls.base}/api/content-scenes`,
      contentSceneId,
      contentSceneObject,
    );
    const response = await axios.put(`${apiUrls.base}/api/content-scenes/${documentId}`, {
      data: { is_start_scene: true },
    });
    return { data: response.data };
  },
);

export const delinkScene = createAsyncThunk(
  "graph/delinkScene",
  async ({ projectId, contentId }: { projectId: number; contentId: number } & IAppLoaderAction) => {
    const response = await axios.post(apiUrls.projects.delinkScene, {
      projectId,
      contentId,
    });
    return { data: response.data };
  },
);

export const undoRelink = createAsyncThunk(
  "graph/undoRelink",
  async ({
    projectId,
    undo,
  }: { projectId: number; undo: RelinkUndoPayload } & IAppLoaderAction) => {
    const response = await axios.post(apiUrls.projects.relinkUndo, {
      projectId,
      undo,
    });
    return { data: response.data };
  },
);

export type UpdateContentTypeParams = {
  contentType: ContentTypeScene | ContentTypeAsset;
  updatedProjectLanguageDatas: Array<{ id: number } & any>;
  changes: any;
  activeLanguageId: number;
  sourceLanguageId: number;
  callback?: any;
  shouldDirtyRefreshOnCompletion?: boolean;
};

export const updateContentType = createAsyncThunk(
  "data/updateContentType",
  async (
    {
      contentType,
      updatedProjectLanguageDatas,
      changes,
      activeLanguageId,
      sourceLanguageId,

      callback = () => {},
      shouldDirtyRefreshOnCompletion = false,
    }: UpdateContentTypeParams & IAppLoaderAction,
    { getState },
  ) => {
    try {
      const isEditingSourceLanguage = Number(activeLanguageId) === Number(sourceLanguageId);
      // here we handle the fact that the 'name' displayed actually belongs to the content (thats also the case in VR)
      const contentId = contentType?.object?.content;
      let baseContentDocumentId: string | undefined;
      const getBaseContentDocumentId = async () => {
        if (!baseContentDocumentId) {
          const state = getState() as RootState;
          baseContentDocumentId = await resolveDocumentId(
            apiUrls.contents,
            contentId,
            state.graphLegacy.contents.entities[contentId],
          );
        }
        return baseContentDocumentId;
      };
      if (isEditingSourceLanguage && contentId && Object.keys(changes).includes("name")) {
        const relevantObjectCurrentName = contentType?.object.name;
        const relevantIncomingName = changes["name"];
        if (relevantObjectCurrentName !== relevantIncomingName) {
          await axios
            .put(`${apiUrls.contents}/${await getBaseContentDocumentId()}`, {
              name: relevantIncomingName,
            })
            .then((res) => res);
        }
      }
      if (isEditingSourceLanguage && contentId) {
        const baseContentPayload: Record<string, unknown> = {};
        for (const k of BASE_CONTENT_FIELD_KEYS) {
          if (Object.prototype.hasOwnProperty.call(changes, k)) {
            baseContentPayload[k] = changes[k];
            delete changes[k];
          }
        }
        if (Object.keys(baseContentPayload).length > 0) {
          await axios
            .put<ContentEntity>(
              `${apiUrls.contents}/${await getBaseContentDocumentId()}`,
              baseContentPayload,
            )
            .then((res) => res);
        }
      }
      const pldPromises = updatedProjectLanguageDatas.map(async (pld) => {
        const pldPayload = omitStrapiEntityMetaKeys({ ...pld });
        const pldDocumentId = await resolveDocumentId(
          apiUrls.projects.projectLanguageDatas,
          pld.id,
          pld.documentId
            ? pld
            : (getState() as RootState).graphLegacy.projectLanguageDatas.entities[pld.id],
        );
        return axios.put<ProjectLanguageDatum>(
          `${apiUrls.projects.projectLanguageDatas}/${pldDocumentId}`,
          {
            data: pldPayload,
          },
        );
      });
      const promiseToUpdateContentType = async () => {
        const collectionUrl = deduceEndpointFromModelName(contentType.modelName);
        const contentTypeDocumentId = await resolveDocumentId(
          collectionUrl,
          contentType.object.id,
          contentType.object as { documentId?: string },
        );
        return axios.put(
          `${collectionUrl}/${contentTypeDocumentId}`,
          stripEmptyMediaRelations(changes),
        );
      };

      const promises: Promise<any>[] = [...pldPromises];
      if (isEditingSourceLanguage) {
        promises.push(promiseToUpdateContentType());
      }
      const responses = await Promise.all(promises);
      const contentTypeResponse = isEditingSourceLanguage ? responses[responses.length - 1] : null;
      callback();

      if (shouldDirtyRefreshOnCompletion) {
        const dirtyLoc = window.location.href;

        window.location.href = dirtyLoc;
      }
      if (contentTypeResponse) {
        return normalizeContentTypeObject(contentTypeResponse.data);
      }
      return null;
    } catch (error) {
      console.error("Error in updateCotentType : ", error);
      throwError(error);
      // Since this is a thunk, and we might not want to throw errors to the Redux middleware,
      // we can return a rejected promise instead to ensure all paths return a value.
      return Promise.reject(error);
    }
  },
);

export const deduceEndpointFromModelName = (modelName: ModelName, id?: number) => {
  // the endpoints are named in a systematic manner, and the rules can be inferred from simple examples
  // - by default, adding a final "s" to the name:
  //   "content-audio" ➡︎ /content-audios
  //   "content-clean" ➡︎ /content-cleans
  // - but beware: when the modelName ends with a number, we add a "-" inbetween
  //   "content-quizz-2" ➡︎ /content-quizz-2-s
  //   "content-button-1" ➡︎ /content-button-1-s
  // - also not that this rule (strangely ?) applies when the number is not at the end:
  //   "content-video-2d" ➡︎ /content-video-2-ds

  // step 1: handle the numbers, if any
  let path = modelName;

  const numberMatch = path.match(/(\d+)([^\d]*)$/);
  if (numberMatch) {
    // If there's a number at the end, add '-' before 's', adjusting for additional non-numeric characters if present
    // ... Well, unless content video 2d, of course 🙈
    if (modelName !== "content-video-2-d") {
      path += numberMatch[2] ? `-${numberMatch[2]}` : "-";
    }
  }
  // ... Sometimes you just have to pile "ifs" to dance around strapi endpoint naming
  if (path.endsWith("catch") || path.endsWith("quizz") || path.endsWith("switch")) {
    path += "e";
  }
  if (modelName === "content-3d-viewer" || modelName === "content-3-d-viewer") {
    path = "content-3-d-viewer";
  }

  path += "s";
  // step 3: when given an id, we suffix
  if (id !== undefined) {
    path += `/${id}`;
  }

  // step 4: finally we prefix with the base URL
  return `${apiUrls.base}/api/${path}`;
};

export const resetGraphInfo = createAction("data/resetGraph");

const graphProjectSlice = createSlice({
  name: "graph/project",
  initialState: null as Project | null,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(loadGraph.fulfilled, (_state, action) => {
      return action?.payload?.data.project;
    });
    builder.addCase(resetGraphInfo, () => null);
  },
});

const actionsAdapter = createEntityAdapter<Action>();

const graphActionsSlice = createSlice({
  name: "graph/actions",
  initialState: actionsAdapter.getInitialState(),
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(loadGraph.fulfilled, (state, action) => {
      try {
        actionsAdapter.setAll(state, action?.payload?.data?.actions ?? {});
      } catch (error) {
        throwError(error);
      }
    });
    builder.addCase(interactionCreatedLocally, (state, action) => {
      actionsAdapter.upsertOne(state, action.payload.action);
    });
    builder.addCase(actionUpsertedLocally, (state, action) => {
      actionsAdapter.upsertOne(state, action.payload);
    });
    builder.addCase(actionDeletedLocally, (state, action) => {
      actionsAdapter.removeOne(state, action.payload);
    });
    builder.addCase(contentDeletedLocally, (state, action) => {
      const contentId = Number(action.payload);
      const doomed = Object.values(state.entities)
        .filter(
          (a: any) =>
            a &&
            (Number(a.affect_content) === contentId || Number(a.affected_content) === contentId),
        )
        .map((a: any) => a.id);
      actionsAdapter.removeMany(state, doomed);
    });
    builder.addCase(resetGraphInfo, (state) => {
      actionsAdapter.removeAll(state);
    });
  },
});

export const { selectAll: selectAllGraphActions } = actionsAdapter.getSelectors(
  (state: RootState) => state.graphLegacy.actions,
);

const contentsAdapter = createEntityAdapter<Content>();

const graphContentsSlice = createSlice({
  name: "graph/contents",
  initialState: contentsAdapter.getInitialState(),
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(loadGraph.fulfilled, (state, action) => {
      contentsAdapter.setAll(state, action?.payload?.data?.contents ?? {});
    });
    builder.addCase(interactionCreatedLocally, (state, action) => {
      contentsAdapter.upsertOne(state, action.payload.content);
    });
    builder.addCase(contentTimecodePatchedLocally, (state, action) => {
      contentsAdapter.updateOne(state, {
        id: action.payload.contentId,
        changes: { time_code: action.payload.timeCode } as any,
      });
    });
    builder.addCase(contentRenamedLocally, (state, action) => {
      contentsAdapter.updateOne(state, {
        id: action.payload.contentId,
        changes: { name: action.payload.name },
      });
    });
    builder.addCase(contentDeletedLocally, (state, action) => {
      contentsAdapter.removeOne(state, Number(action.payload));
    });
    builder.addCase(resetGraphInfo, (state) => {
      contentsAdapter.removeAll(state);
    });
  },
});

export const { selectAll: selectAllGraphContents, selectEntities: selectGraphContentByIds } =
  contentsAdapter.getSelectors((state: RootState) => state.graphLegacy.contents);

export const fetchMapContentKeysWithPldKeys = createAsyncThunk(
  "data/getMapContentKeysWithPldKeys",
  async () => {
    const response = await axios.get(
      `${apiUrls.projects.projectLanguageDatas}/mapContentKeysWithPldKeys`,
    );
    return response.data;
  },
  {
    condition: (_arg, { getState }) => {
      const state = getState() as RootState;
      return (
        Object.keys(state.graphLegacy.projectLanguageDatas.mapContentKeysWithPldKeys).length === 0
      );
    },
  },
);

export const refetchPldsForContent = createAsyncThunk(
  "data/refetchPldsForContent",
  async (contentId: number) => {
    const response = await axios.get(
      `${apiUrls.projects.projectLanguageDatas}?filters[content][$eq]=${contentId}&pagination[pageSize]=100`,
    );
    const raw: any[] = response.data?.data ?? [];
    return raw.map((row: any) => {
      const attrs = row?.attributes ?? row;
      const flat: any = { id: row.id, ...attrs };
      if (attrs?.content?.data?.id != null) flat.content = attrs.content.data.id;
      if (attrs?.language?.data?.id != null) flat.language = attrs.language.data.id;
      return flat as IProjectLanguageData;
    });
  },
);

const projectLanguageDatasAdapter = createEntityAdapter<IProjectLanguageData>();

const initialState = projectLanguageDatasAdapter.getInitialState({
  currentLanguagePldsMappedByContent: {},
  mapContentKeysWithPldKeys: {},
  relevantPldAfterUpdate: {},
  loadedPldLanguageIds: [] as number[],
});

const markPldLanguagesLoaded = (state: { loadedPldLanguageIds: number[] }, ids: number[]) => {
  const merged = new Set(state.loadedPldLanguageIds);
  for (const id of ids) if (Number.isFinite(id)) merged.add(Number(id));
  state.loadedPldLanguageIds = [...merged];
};

export const getMapContentKeysWithPldKeys = (state: RootState) =>
  state.graphLegacy.projectLanguageDatas.mapContentKeysWithPldKeys;

export const { selectAll: selectAllGraphPlds } = projectLanguageDatasAdapter.getSelectors(
  (state: RootState) => state.graphLegacy.projectLanguageDatas,
);

export const selectLoadedPldLanguageIds = (state: RootState): number[] =>
  state.graphLegacy.projectLanguageDatas.loadedPldLanguageIds;

const graphProjectLanguageDatasSlice = createSlice({
  name: "graph/projectLanguageDatas",
  initialState,
  reducers: {
    resetProjectLanguageDatas: (state) => {
      projectLanguageDatasAdapter.removeAll(state);
      state.currentLanguagePldsMappedByContent = {};
      state.loadedPldLanguageIds = [];
    },
    updateRelevantPld: (state, action: PayloadAction<{ contentId: string; updatedPld: any }>) => {
      const { contentId, updatedPld } = action.payload;
      state.currentLanguagePldsMappedByContent[contentId] = {
        ...state.currentLanguagePldsMappedByContent[contentId],
        projectLanguageData: updatedPld,
      };
    },

    setRelevantPldAfterUpdate: (state, action: PayloadAction<IProjectLanguageData>) => {
      state.relevantPldAfterUpdate = action.payload;
    },

    patchPlds: (
      state,
      action: PayloadAction<Array<{ id: number; changes: Partial<IProjectLanguageData> }>>,
    ) => {
      projectLanguageDatasAdapter.updateMany(state, action.payload);
      action.payload.forEach(({ id, changes }) => {
        const updated = state.entities[id];
        if (!updated) return;
        const contentId = (updated as any).content;
        const existing = state.currentLanguagePldsMappedByContent[contentId];
        if (existing && Number(existing.projectLanguageData?.id) === Number(id)) {
          state.currentLanguagePldsMappedByContent[contentId] = {
            ...existing,
            projectLanguageData: { ...existing.projectLanguageData, ...changes },
          };
        }
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadGraph.fulfilled, (state, action) => {
      projectLanguageDatasAdapter.setAll(state, action.payload?.data?.projectLanguageDatas);

      // Assuming project and contents are also loaded here
      const project = action.payload?.data.project;
      const contents = action.payload?.data.contents.reduce((acc, content) => {
        acc[content.id] = content;
        return acc;
      }, {});

      // Reset currentLanguagePldsMappedByContent before populating
      state.currentLanguagePldsMappedByContent = {};
      const primaryLanguageId = resolvePrimaryLanguageId(project);
      state.loadedPldLanguageIds = primaryLanguageId != null ? [Number(primaryLanguageId)] : [];

      Object.values(state.entities).forEach((pld) => {
        const projectLanguageId =
          typeof project.language === "object" && project.language !== null
            ? project.language.id
            : project.language;
        if (project && pld.language === projectLanguageId) {
          state.currentLanguagePldsMappedByContent[pld.content] = {
            languageId: projectLanguageId,
            content: contents[pld.content],
            projectLanguageData: pld,
          };
        }
      });
    });

    builder.addCase(interactionCreatedLocally, (state, action) => {
      const { plds, content, projectLanguageId } = action.payload;
      if (!plds?.length) return;
      projectLanguageDatasAdapter.upsertMany(state, plds);
      const currentPld = plds.find(
        (p) => projectLanguageId != null && Number(p.language) === Number(projectLanguageId),
      );
      if (currentPld) {
        state.currentLanguagePldsMappedByContent[currentPld.content] = {
          languageId: Number(projectLanguageId),
          content,
          projectLanguageData: currentPld,
        };
      }
    });

    builder.addCase(fetchMapContentKeysWithPldKeys.fulfilled, (state, action) => {
      state.mapContentKeysWithPldKeys = action.payload;
    });

    builder.addCase(loadGraphLanguage.fulfilled, (state, action) => {
      markPldLanguagesLoaded(state, [action.meta.arg.languageId]);
      const rows = action.payload?.projectLanguageDatas ?? [];
      if (rows.length === 0) return;
      projectLanguageDatasAdapter.upsertMany(state, rows);
    });

    builder.addCase(loadGraphLanguages.fulfilled, (state, action) => {
      markPldLanguagesLoaded(state, action.meta.arg.languageIds);
      const rows = action.payload?.projectLanguageDatas ?? [];
      if (rows.length === 0) return;
      projectLanguageDatasAdapter.upsertMany(state, rows);
    });

    builder.addCase(updateContentType.fulfilled, (state, action) => {
      const updatedPlds = action.meta?.arg?.updatedProjectLanguageDatas ?? [];
      const updates = updatedPlds
        .filter((pld: any) => pld?.id != null)
        .map((pld: any) => {
          const { id, ...changes } = pld;
          return { id, changes };
        });
      if (updates.length === 0) return;
      projectLanguageDatasAdapter.updateMany(state, updates);
      updates.forEach(({ id, changes }) => {
        const updated = state.entities[id];
        if (!updated) return;
        const contentId = (updated as any).content;
        const existing = state.currentLanguagePldsMappedByContent[contentId];
        if (existing && Number(existing.projectLanguageData?.id) === Number(id)) {
          state.currentLanguagePldsMappedByContent[contentId] = {
            ...existing,
            projectLanguageData: { ...existing.projectLanguageData, ...changes },
          };
        }
      });
    });

    builder.addCase(refetchPldsForContent.fulfilled, (state, action) => {
      projectLanguageDatasAdapter.upsertMany(state, action.payload);
      action.payload.forEach((pld: any) => {
        const contentId = pld.content;
        const existing = state.currentLanguagePldsMappedByContent[contentId];
        if (existing && Number(existing.projectLanguageData?.id) === Number(pld.id)) {
          state.currentLanguagePldsMappedByContent[contentId] = {
            ...existing,
            projectLanguageData: pld,
          };
        }
      });
    });

    builder.addCase(resetGraphInfo, (state) => {
      projectLanguageDatasAdapter.removeAll(state);
      state.currentLanguagePldsMappedByContent = {};
      // We want to persist the mapContentKeysWithPldKeys
      const preservedMapContentKeysWithPldKeys = state.mapContentKeysWithPldKeys;
      state.mapContentKeysWithPldKeys = preservedMapContentKeysWithPldKeys;
    });
  },
});

export const { updateRelevantPld, patchPlds } = graphProjectLanguageDatasSlice.actions;

const makeContentTypeUniqueKey = (contentType: ContentType) => {
  try {
    const x = `${contentType.modelName}-${contentType.object.id}`;
    return x;
  } catch (error) {
    throwError(error);
    return null;
  }
};

const contentTypesAdapter = createEntityAdapter<ContentType>({
  selectId: makeContentTypeUniqueKey as any,
});

const graphContentTypesSlice = createSlice({
  name: "graph/contentTypes",
  initialState: contentTypesAdapter.getInitialState(),
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(loadGraph.fulfilled, (state, action) => {
      try {
        const incoming = action?.payload?.data?.contentTypes ?? [];
        contentTypesAdapter.setAll(state, incoming.filter(hasContentTypeObject));
        // here: create a new state.graphLegacy.projectLanguageData to store them.
      } catch (error) {
        throwError(error);
      }
    });
    builder.addCase(interactionCreatedLocally, (state, action) => {
      contentTypesAdapter.upsertOne(state, {
        modelName: action.payload.modelName,
        object: action.payload.interaction,
      } as ContentType);
    });
    builder.addCase(contentDeletedLocally, (state, action) => {
      const contentId = Number(action.payload);
      const doomedKeys = Object.entries(state.entities)
        .filter(([, ct]: [string, any]) => Number(ct?.object?.content) === contentId)
        .map(([key]) => key);
      contentTypesAdapter.removeMany(state, doomedKeys);
    });
    builder.addCase(contentRenamedLocally, (state, action) => {
      const contentId = Number(action.payload.contentId);
      for (const ct of Object.values(state.entities)) {
        const object = ct?.object as { content?: number; name?: string } | undefined;
        if (!object || Number(object.content) !== contentId) continue;
        object.name = action.payload.name;
      }
    });
    builder.addCase(updateContentType.fulfilled, (state, action) => {
      try {
        if (!action.payload) return;
        // ⚠️ the endpoint returns the content type OBJECT !
        // we need to "rebuild" a content-type from the result and the modelName of the args
        const contentTypeObject =
          action?.payload?.entities.contentTypeObjects[action.payload.result];
        const { modelName } = action.meta.arg.contentType;
        const contentType = { modelName, object: contentTypeObject } as ValidContentType;

        // then we can update the local valu
        contentTypesAdapter.updateOne(state, {
          id: makeContentTypeUniqueKey(contentType) as any,
          changes: contentType,
        });
      } catch (error) {
        throwError(error);
      }
    });
    builder.addCase(updateContentType.rejected, (_state, action) => {
      try {
        return action.meta.arg.callback && typeof action.meta.arg.callback === "function"
          ? action.meta.arg.callback()
          : null;
      } catch (error) {
        throwError(error);
      }
    });
    builder.addCase(resetGraphInfo, (state) => {
      try {
        contentTypesAdapter.removeAll(state);
      } catch (error) {
        throwError(error);
      }
    });
  },
});

export const { selectAll: selectAllGraphContentTypes } = contentTypesAdapter.getSelectors(
  (state: RootState) => state.graphLegacy.contentTypes,
);

const selectedContentSlice = createSlice({
  name: "graph/selectedContent",
  initialState: null as number | null,
  reducers: {
    /// defines a project as "the active project"
    setSelectedContent: (_state: any, action: PayloadAction<number | null>) => action.payload,
  },
  extraReducers: (builder) => {
    builder.addCase(resetGraphInfo, () => null);
  },
});

export const selectSelectedContentId = (state: RootState) => state.graphLegacy.selectedContent;
export const projectContents = (state: RootState) => state.graphLegacy.contents;

const graphErrorMessagesSlice = createSlice({
  name: "graph/errorMessages",
  initialState: [] as { projectId: number; messages: string[] }[],
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(loadGraph.fulfilled, (state, action) => {
      const projectId = action.payload.data.project.id;
      const newErrorMessages = action.payload.data.errorMessages || [];
      const existingEntry = state.find((entry) => entry.projectId === projectId);
      if (existingEntry) {
        existingEntry.messages = newErrorMessages;
      } else {
        state.push({ projectId, messages: newErrorMessages });
      }
    });
    builder.addCase(checkValidityOnly.fulfilled, (state, action) => {
      const projectId = action.payload.data.project.id;
      const newErrorMessages = action.payload.data.errorMessages || [];
      const existingEntry = state.find((entry) => entry.projectId === projectId);
      if (existingEntry) {
        existingEntry.messages = newErrorMessages;
      } else {
        state.push({ projectId, messages: newErrorMessages });
      }
    });
    builder.addCase(resetGraphInfo, () => []);
  },
});

const loading = (state = false, action: any) => {
  if (loadGraph.pending.match(action) || updateContentType.pending.match(action)) {
    return false;
  } else if (loadGraph.rejected.match(action) || loadGraph.fulfilled.match(action)) {
    return false;
  } else if (
    updateContentType.rejected.match(action) ||
    updateContentType.fulfilled.match(action)
  ) {
    return false;
  } else if (resetGraphInfo.match(action)) {
    return false;
  }
  return state;
};

// Aurelien's error handling (see https://redux-toolkit.js.org/api/createAsyncThunk#type )
const error = (state: Error | null = null, action: any) => {
  if (loadGraph.pending.match(action) || loadGraph.fulfilled.match(action)) {
    return null;
  } else if (loadGraph.rejected.match(action)) {
    throwError(action.error);
    return action.error;
  } else if (updateContentType.pending.match(action) || updateContentType.fulfilled.match(action)) {
    return null;
  } else if (updateContentType.rejected.match(action)) {
    throwError(action.error);
    return action.error;
  } else if (resetGraphInfo.match(action)) {
    return null;
  }
  return state;
};

const graph = combineReducers({
  loading,
  error,
  selectedContent: selectedContentSlice.reducer,
  project: graphProjectSlice.reducer,
  actions: graphActionsSlice.reducer,
  contents: graphContentsSlice.reducer,
  contentTypes: graphContentTypesSlice.reducer,
  projectLanguageDatas: graphProjectLanguageDatasSlice.reducer,
  errorMessages: graphErrorMessagesSlice.reducer,
});

export type Graph = ReturnType<typeof graph>;

import { getShowAllAssets } from "./graphuSlice";

export const getGraphLayout = createSelector(
  [selectAllGraphContents, selectAllGraphContentTypes, selectAllGraphActions, getShowAllAssets],
  (contents, contentTypes, actions, showAllAssets) =>
    buildGraph(contents, contentTypes, actions, { showAllAssets }),
);

export const getNodeById = (state: RootState, nodeId: string, modelName?: string) => {
  const allContentTypes = selectAllGraphContentTypes(state);
  return allContentTypes.find(
    (node) =>
      Number(node.object.id) === Number(nodeId) && (node.modelName === modelName || !modelName),
  );
};

export const relevantPldForContent = (contentId) =>
  createSelector(
    (state: RootState) => state.graphLegacy.projectLanguageDatas.currentLanguagePldsMappedByContent,
    (pldsMappedByContent) => {
      const pld = pldsMappedByContent[contentId];
      if (!pld) {
        console.error(
          `No project language data found for content ID ${contentId}; pldsMappedByContent:`,
          pldsMappedByContent,
        );
        return undefined;
      }
      return pld;
    },
  );

export const relevantPldsForContent = (contentId) =>
  createSelector(
    (state: RootState) => state.graphLegacy.projectLanguageDatas.entities,
    (state: RootState) => state.projects.currentProject?.enabled_languages,
    (pldEntities, enabledLanguages) => {
      const loadedPlds = Object.values(pldEntities).filter(
        (pld) => Number(pld.content) === Number(contentId),
      );

      if (!enabledLanguages || enabledLanguages.length === 0) {
        if (loadedPlds.length === 0) {
          console.error(`No project language data found for content ID ${contentId}`);
          return undefined;
        }
        return loadedPlds;
      }

      const loadedByLanguageId = new Map(loadedPlds.map((pld) => [Number(pld.language), pld]));
      const plds = enabledLanguages.map((language) => {
        const languageId = Number(language.id);
        return (
          loadedByLanguageId.get(languageId) ??
          ({ content: Number(contentId), language: languageId } as IProjectLanguageData)
        );
      });

      if (plds.length === 0) {
        console.error(`No project language data found for content ID ${contentId}`);
        return undefined;
      }
      return plds;
    },
  );

export default graph;
